import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import {
  CurrencyInput,
  DocumentNumberField,
  FormFieldV2 as FormField,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import type { GiayBaoNoSearchFieldActions, GiayBaoNoSearchFieldState } from '../../hooks/useSearchFieldStates';
import { accountSearchColumns, MA_CHUNG_TU, QUERY_KEYS, taiKhoanNganHangSearchColumns } from '@/constants';
import { AccountModel } from '@/types/schemas';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: GiayBaoNoSearchFieldState;
    actions: GiayBaoNoSearchFieldActions;
  };
}

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const { setValue } = useFormContext();
  const ngay_ct = useWatch({ name: 'ngay_ct' });

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_lct', ngay_ct);
    }
  }, [ngay_ct, setValue]);
  return (
    <div className='space-y-2 p-4'>
      <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
        <div className='flex-1 space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Loại chứng từ</Label>
            <div className='w-[260px]'>
              <FormField
                type='select'
                options={[
                  { value: '1', label: '1. Chi theo hóa đơn' },
                  { value: '2', label: '2. Chi theo đối tượng' },
                  { value: '3', label: '3. Chi khác' },
                  { value: '4', label: '4. Rút tiền về nhập quỹ' },
                  { value: '5', label: '5. Chuyển giữa các ngân hàng' }
                ]}
                name='ma_ngv'
                value={state.loaiChungTu}
                onValueChange={actions.setLoaiChungTu}
                disabled={formMode === 'view'}
              />
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField type='text' name='dia_chi' disabled={formMode === 'view'} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Người nhận tiền</Label>
            <div className='flex-1'>
              <FormField type='text' name='ong_ba' disabled={formMode === 'view'} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField type='text' name='dien_giai' disabled={formMode === 'view'} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngân hàng</Label>
            <SearchField
              type='text'
              value={state.bank?.tknh}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}`}
              searchColumns={taiKhoanNganHangSearchColumns}
              columnDisplay='tknh'
              displayRelatedField='name'
              relatedFieldValue={state.bank?.name || ''}
              dialogTitle='Danh mục tài khoản ngân hàng'
              onRowSelection={actions.setBank}
              disabled={formMode === 'view'}
            />
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Tài khoản có</Label>
            <SearchField<AccountModel>
              type='text'
              value={state.account?.code || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              displayRelatedField='name'
              relatedFieldValue={state.account?.name || ''}
              dialogTitle='Danh mục tài khoản'
              onRowSelection={actions.setAccount}
              disabled={formMode === 'view'}
            />
          </div>
        </div>

        <div className='w-full space-y-3 lg:w-[25rem]'>
          <DocumentNumberField
            ma_ct={MA_CHUNG_TU.TIEN_GUI.GIAY_BAO_NO}
            quyenChungTu={state.quyenChungTu}
            onQuyenChungTuChange={actions.setQuyenChungTu}
            soChungTu={state.soChungTu || ''}
            onSoChungTuChange={actions.setSoChungTu}
            disabled={formMode === 'view'}
            classNameSearchField='w-full'
            formMode={formMode}
          />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngày chứng từ</Label>
            <div className='flex-1'>
              <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngày lập chứng từ</Label>
            <div className='flex-1'>
              <FormField name='ngay_lct' type='date' disabled={formMode === 'view'} />
            </div>
          </div>

          <CurrencyInput formMode={formMode} />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Trạng thái</Label>
            <div className='flex-1'>
              <FormField
                name='status'
                type='select'
                disabled={formMode === 'view'}
                options={[
                  { value: '0', label: 'Lập chứng từ' },
                  { value: '1', label: 'Chờ duyệt' },
                  { value: '5', label: 'Chi tiền' }
                ]}
              />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <FormField label='Dữ liệu nhận được' name='transfer_yn' type='checkbox' disabled={true} />
          </div>
        </div>
      </div>
    </div>
  );
}
